import React, { useState, useEffect } from "react";
import { Target, Zap, BarChart3, Sparkles } from "lucide-react";
import SEO, { PAGE_SEO } from "./SEO";
import { UserProfile } from "../types";
import { aiService } from "../services/aiService";
import { matchBallToPlayer } from "../data/ballDatabase";

interface BallMatchingProps {
  userProfile: UserProfile | null;
}

interface BallRecommendation {
  brand: string;
  model: string;
  type: string;
  compression: number;
  dimples: number;
  construction: string;
  features: string[];
  matchScore: number;
  reasons: string[];
  price: string;
}

const BallMatching: React.FC<BallMatchingProps> = ({ userProfile }) => {
  const [recommendations, setRecommendations] = useState<BallRecommendation[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Map user's equipment tier to ball budget format
  const getUserBudget = () => {
    if (!userProfile?.equipmentTier) return "mid-range";
    const tierMap: Record<string, string> = {
      'budget': 'budget',
      'medium': 'mid-range',
      'premium': 'premium'
    };
    return tierMap[userProfile.equipmentTier] || "mid-range";
  };
  
  const [customSettings, setCustomSettings] = useState({
    swingSpeed: userProfile?.swingSpeed || 95,
    preferredFeel: "medium",
    primaryGoal: "distance",
    budget: getUserBudget()
  });

  // Sync with profile when it changes
  useEffect(() => {
    if (userProfile?.swingSpeed) {
      setCustomSettings(prev => ({
        ...prev,
        swingSpeed: userProfile.swingSpeed!
      }));
    }
  }, [userProfile?.swingSpeed]);

  // Auto-generate recommendations on component mount
  useEffect(() => {
    generateRecommendations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateRecommendations = async () => {
    setLoading(true);
    
    try {
      // Use AI for ball recommendations if profile available
      if (userProfile) {
        const aiRecommendations = await aiService.getAIBallRecommendations({
          userProfile,
          swingSpeed: customSettings.swingSpeed,
          preferredFeel: customSettings.preferredFeel,
          primaryGoal: customSettings.primaryGoal,
          budget: customSettings.budget
        });
        
        setRecommendations(aiRecommendations);
        setLoading(false);
        return;
      }
    } catch (error) {
      console.error("AI ball recommendations failed, using fallback:", error);
    }
    
    // Fallback to comprehensive ball database
    setTimeout(() => {
      // Use comprehensive ball database with intelligent matching
      const matchedBalls = matchBallToPlayer({
        swingSpeed: customSettings.swingSpeed,
        handicap: userProfile?.handicap || 18,
        budget: customSettings.budget === 'budget' ? 25 : 
                customSettings.budget === 'mid-range' ? 45 : 60,
        preferredFeel: customSettings.preferredFeel as any
      });

      // Take top 5 matches
      const topMatches = matchedBalls.slice(0, 5);

      // Convert to BallRecommendation format
      const recommendations: BallRecommendation[] = topMatches.map((ball, index) => ({
        brand: ball.brand,
        model: ball.model,
        type: ball.category.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        compression: ball.compression,
        dimples: ball.dimples,
        construction: ball.construction,
        features: ball.keyFeatures,
        matchScore: 95 - (index * 5), // Descending match score
        reasons: ball.bestFor.slice(0, 3),
        price: `$${ball.pricePerDozen.typical}/dozen`
      }));

      setRecommendations(recommendations);
      setLoading(false);
    }, 1500);
  };

  const handleInputChange = (field: string, value: any) => {
    setCustomSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <>
      <SEO {...PAGE_SEO.ballMatching} url="/ball-matching" />
    <div className="animate-fade-in max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div
        className="animate-fade-in"
        style={{
          marginBottom: "2rem",
          background: "linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(59, 130, 246, 0.1) 50%, rgba(147, 51, 234, 0.1) 100%)",
          borderRadius: "2rem",
          padding: "2rem 1.5rem",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "1rem", marginBottom: "1rem" }}>
            <div
              style={{
                padding: "1.25rem",
                borderRadius: "2rem",
                background: "linear-gradient(135deg, #22c55e, #16a34a)",
                color: "white",
                boxShadow: "0 12px 30px rgba(34, 197, 94, 0.4)",
                animation: "float 3s ease-in-out infinite",
              }}
            >
              <Target size={32} />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            🎯 AI Ball Performance Matching
          </h1>
          <p className="text-gray-600 text-lg">
            Find the perfect golf ball for your swing and playing style
          </p>
        </div>
      </div>

      {/* Settings Panel */}
      <div className="card p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 size={20} />
          Your Swing Profile
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Swing Speed (mph)
            </label>
            <input
              type="number"
              value={customSettings.swingSpeed}
              onChange={(e) => handleInputChange('swingSpeed', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              min="60"
              max="130"
            />
            {userProfile?.swingSpeed && (
              <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Pre-filled from your profile
              </p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferred Feel
            </label>
            <select
              value={customSettings.preferredFeel}
              onChange={(e) => handleInputChange('preferredFeel', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="soft">Soft</option>
              <option value="medium">Medium</option>
              <option value="firm">Firm</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Primary Goal
            </label>
            <select
              value={customSettings.primaryGoal}
              onChange={(e) => handleInputChange('primaryGoal', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="distance">Maximum Distance</option>
              <option value="control">Greenside Control</option>
              <option value="spin">Spin Control</option>
              <option value="durability">Durability</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Budget Range
            </label>
            <select
              value={customSettings.budget}
              onChange={(e) => handleInputChange('budget', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="budget">Budget ($20-35/dozen)</option>
              <option value="mid-range">Mid-Range ($35-50/dozen)</option>
              <option value="premium">Premium ($50+/dozen)</option>
            </select>
          </div>
        </div>

        <button
          onClick={generateRecommendations}
          disabled={loading}
          className="w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Analyzing Your Profile...
            </>
          ) : (
            <>
              <Zap size={20} />
              {recommendations.length > 0 ? 'Refresh Recommendations' : 'Find My Perfect Ball'}
            </>
          )}
        </button>
      </div>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            🏆 Your Personalized Ball Recommendations
          </h3>
          
          {recommendations.map((ball, index) => (
            <div key={index} className="card p-6 border-l-4 border-green-500">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">
                    {ball.brand} {ball.model}
                  </h4>
                  <p className="text-sm text-gray-600">{ball.type}</p>
                </div>
                <div className="text-right">
                  <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                    {ball.matchScore}% Match
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{ball.price}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">Compression</p>
                  <p className="text-lg font-semibold text-gray-900">{ball.compression}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Construction</p>
                  <p className="text-lg font-semibold text-gray-900">{ball.construction}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Dimples</p>
                  <p className="text-lg font-semibold text-gray-900">{ball.dimples}</p>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Key Features</p>
                <div className="flex flex-wrap gap-2">
                  {ball.features.map((feature, idx) => (
                    <span key={idx} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                      {feature}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Why This Ball?</p>
                <ul className="space-y-1">
                  {ball.reasons.map((reason, idx) => (
                    <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>
                      {reason}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    </>
  );
};

export default BallMatching;
